import type { CSSProperties, FC, ReactNode } from 'react'
import type { Team, TeamId } from '@/types'

import { useState } from 'react'
import { User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { COORDINATOR_COLOR, coordinator, teams } from '@/constants/agents'

interface OrbitHubProps {
    step: number
    focusTeam: TeamId | null
    activeId: string | null
    paused: boolean
    reducedMotion: boolean
    onHover: (id: string | null) => void
    onSelect: (id: string) => void
}

interface Point {
    x: number
    y: number
}

const CENTER: Point = { x: 50, y: 50 }
const YOU: Point = { x: 8, y: 8 }
const RING_RADII = [19, 27.5, 36, 44.5]
const RING_DURATIONS = [70, 95, 125, 160]

const round = (value: number): number => Math.round(value * 1000) / 1000

const rings = teams.map((team, ringIndex) => {
    const radius = RING_RADII[ringIndex]
    const offset = -90 + ringIndex * 18
    const reverse = ringIndex % 2 === 1

    const nodes = team.agents.map((agent, agentIndex) => {
        const angle = ((offset + (360 / team.agents.length) * agentIndex) * Math.PI) / 180

        return {
            agent,
            point: {
                x: round(CENTER.x + radius * Math.cos(angle)),
                y: round(CENTER.y + radius * Math.sin(angle))
            }
        }
    })

    const style = {
        '--orbit-duration': `${RING_DURATIONS[ringIndex]}s`,
        '--orbit-direction': reverse ? 'reverse' : 'normal',
        '--orbit-counter-direction': reverse ? 'normal' : 'reverse'
    } as CSSProperties

    return { team, radius, nodes, style, ringIndex }
})

interface PulseProps {
    from: Point
    to: Point
    color: string
    duration: number
}

const Pulse: FC<PulseProps> = ({ from, to, color, duration }): ReactNode => {
    const dur = `${duration}s`

    return (
        <circle r='0.9' fill={color} opacity='0'>
            <animateMotion
                dur={dur}
                repeatCount='indefinite'
                path={`M${from.x} ${from.y} L${to.x} ${to.y}`}
            />
            <animate
                attributeName='opacity'
                values='0;1;1;0'
                keyTimes='0;0.15;0.8;1'
                dur={dur}
                repeatCount='indefinite'
            />
        </circle>
    )
}

const OrbitHub: FC<OrbitHubProps> = ({
    step,
    focusTeam,
    activeId,
    paused,
    reducedMotion,
    onHover,
    onSelect
}): ReactNode => {
    const [hovering, setHovering] = useState(false)

    const isTeamLit = (team: Team): boolean => !focusTeam || focusTeam === team.id
    const youLit = step === 0 || step === 3
    const coordinatorActive = activeId === 'coordinator' || step === 1
    const showPulses = !reducedMotion
    const CoordinatorIcon = coordinator.icon

    return (
        <div
            className='orbit relative mx-auto aspect-square w-full max-w-[640px] select-none'
            data-paused={paused || hovering ? 'true' : 'false'}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => {
                setHovering(false)
                onHover(null)
            }}
            onFocusCapture={() => setHovering(true)}
            onBlurCapture={() => setHovering(false)}
        >
            <div
                aria-hidden='true'
                className='absolute inset-[16%] rounded-full bg-[radial-gradient(circle,oklch(0.74_0.16_292/0.22),transparent_70%)] blur-2xl'
            />

            <svg
                aria-hidden='true'
                viewBox='0 0 100 100'
                className='pointer-events-none absolute inset-0 size-full overflow-visible'
            >
                <line
                    x1={YOU.x}
                    y1={YOU.y}
                    x2={CENTER.x}
                    y2={CENTER.y}
                    stroke={COORDINATOR_COLOR}
                    strokeOpacity={youLit ? 0.75 : 0.2}
                    strokeWidth={1.25}
                    strokeDasharray='3 4'
                    vectorEffect='non-scaling-stroke'
                    className='transition-[stroke-opacity] duration-500'
                />

                {showPulses && step === 0 && (
                    <>
                        <Pulse from={YOU} to={CENTER} color='#ffffff' duration={1.6} />
                        <Pulse from={YOU} to={CENTER} color={COORDINATOR_COLOR} duration={2.3} />
                    </>
                )}

                {showPulses && step === 3 && (
                    <>
                        <Pulse from={CENTER} to={YOU} color='#ffffff' duration={1.6} />
                        <Pulse from={CENTER} to={YOU} color={COORDINATOR_COLOR} duration={2.3} />
                    </>
                )}
            </svg>

            {rings.map(({ team, radius, nodes, style, ringIndex }) => {
                const lit = isTeamLit(team)
                const working = lit && step >= 2

                return (
                    <div
                        key={`${team.id}-lines`}
                        aria-hidden='true'
                        className='orbit-spin pointer-events-none absolute inset-0'
                        style={style}
                    >
                        <svg viewBox='0 0 100 100' className='absolute inset-0 size-full overflow-visible'>
                            <circle
                                cx={CENTER.x}
                                cy={CENTER.y}
                                r={radius}
                                fill='none'
                                stroke={team.color}
                                strokeOpacity={lit ? (working ? 0.55 : 0.32) : 0.1}
                                strokeWidth={1}
                                strokeDasharray='2 5'
                                vectorEffect='non-scaling-stroke'
                                className='transition-[stroke-opacity] duration-500'
                            />

                            {nodes.map(({ agent, point }, agentIndex) => {
                                const isActive = activeId === agent.id
                                const spokeLit = working || isActive
                                const duration = round(1.4 + agentIndex * 0.17 + ringIndex * 0.11)

                                return (
                                    <g key={agent.id}>
                                        <line
                                            x1={CENTER.x}
                                            y1={CENTER.y}
                                            x2={point.x}
                                            y2={point.y}
                                            stroke={team.color}
                                            strokeOpacity={spokeLit ? 0.5 : 0.07}
                                            strokeWidth={1}
                                            vectorEffect='non-scaling-stroke'
                                            className='transition-[stroke-opacity] duration-500'
                                        />

                                        {showPulses && (working || isActive) && (
                                            step === 3 && !isActive
                                                ? <Pulse from={point} to={CENTER} color={team.color} duration={duration} />
                                                : <Pulse from={CENTER} to={point} color={team.color} duration={duration} />
                                        )}
                                    </g>
                                )
                            })}
                        </svg>
                    </div>
                )
            })}

            {rings.map(({ team, nodes, style }) => {
                const lit = isTeamLit(team)

                return (
                    <div
                        key={`${team.id}-nodes`}
                        className='orbit-spin pointer-events-none absolute inset-0'
                        style={style}
                    >
                        {nodes.map(({ agent, point }) => {
                            const Icon = agent.icon
                            const isActive = activeId === agent.id
                            const dimmed = !lit || (step < 2 && !isActive)

                            return (
                                <button
                                    key={agent.id}
                                    type='button'
                                    aria-label={`${agent.name}, ${team.name} team`}
                                    aria-pressed={isActive}
                                    onMouseEnter={() => onHover(agent.id)}
                                    onFocus={() => onHover(agent.id)}
                                    onBlur={() => onHover(null)}
                                    onClick={() => onSelect(agent.id)}
                                    className='pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline-none'
                                    style={{ left: `${point.x}%`, top: `${point.y}%` }}
                                >
                                    <span className='orbit-counter relative block'>
                                        <span
                                            className={cn(
                                                'flex size-7 items-center justify-center rounded-full border bg-background transition-all duration-300 sm:size-9 lg:size-10',
                                                isActive && 'scale-125',
                                                !lit ? 'opacity-25' : dimmed ? 'opacity-60' : 'opacity-100'
                                            )}
                                            style={{
                                                borderColor: team.color,
                                                color: team.color,
                                                backgroundImage: `radial-gradient(circle, ${team.color}33, ${team.color}0d)`,
                                                boxShadow: isActive ? `0 0 0 4px ${team.color}33, 0 0 28px ${team.color}99` : `0 0 14px ${team.color}26`
                                            }}
                                        >
                                            <Icon className='size-3.5 sm:size-4' aria-hidden='true' />
                                        </span>

                                        <span
                                            className={cn(
                                                'pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-popover/95 px-2 py-1 text-[11px] font-medium text-foreground shadow-lg backdrop-blur transition-opacity duration-200',
                                                isActive ? 'opacity-100' : 'opacity-0'
                                            )}
                                        >
                                            {agent.name}
                                        </span>
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                )
            })}

            <button
                type='button'
                aria-label='Coordinator, the agent you talk to'
                aria-pressed={activeId === 'coordinator'}
                onMouseEnter={() => onHover('coordinator')}
                onFocus={() => onHover('coordinator')}
                onBlur={() => onHover(null)}
                onClick={() => onSelect('coordinator')}
                className='absolute left-1/2 top-1/2 aspect-square w-[21%] -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline-none'
            >
                <span
                    aria-hidden='true'
                    className={cn(
                        'orbit-breathe absolute -inset-[32%] rounded-full bg-[radial-gradient(circle,oklch(0.74_0.16_292/0.55),transparent_68%)] transition-opacity duration-500',
                        coordinatorActive ? 'opacity-100' : 'opacity-70'
                    )}
                />

                <span
                    aria-hidden='true'
                    className={cn(
                        'orbit-spin absolute -inset-[14%] rounded-full border border-dashed border-violet-300/60 transition-opacity duration-500',
                        coordinatorActive ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{ '--orbit-duration': '14s' } as CSSProperties}
                />

                <span
                    className={cn(
                        'relative flex size-full flex-col items-center justify-center gap-0.5 rounded-full border border-white/25 bg-[radial-gradient(circle_at_35%_30%,#ddd0ff,#8b5cf6_55%,#4c1d95)] text-white transition-transform duration-500',
                        coordinatorActive && 'scale-105'
                    )}
                    style={{ boxShadow: `0 0 48px ${COORDINATOR_COLOR}88, inset 0 0 18px #ffffff40` }}
                >
                    <CoordinatorIcon className='size-5 sm:size-7' aria-hidden='true' />
                    <span className='font-heading text-[9px] font-semibold tracking-wide sm:text-xs'>
                        Coordinator
                    </span>
                </span>
            </button>

            <div
                className='pointer-events-none absolute -translate-x-1/2 -translate-y-1/2'
                style={{ left: `${YOU.x}%`, top: `${YOU.y}%` }}
            >
                <span
                    className={cn(
                        'flex size-9 items-center justify-center rounded-full border bg-background text-foreground transition-all duration-500 sm:size-11',
                        youLit ? 'border-white/70 shadow-[0_0_28px_oklch(1_0_0/0.35)]' : 'border-white/20'
                    )}
                >
                    <User className='size-4 sm:size-5' aria-hidden='true' />
                </span>

                <span className='absolute left-1/2 top-full mt-1.5 -translate-x-1/2 text-[11px] font-medium text-muted-foreground'>
                    You
                </span>
            </div>

            <div
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute left-[14%] top-[2.5%] hidden max-w-[44%] rounded-xl rounded-tl-sm border border-white/10 bg-popover/90 px-3 py-2 text-xs leading-snug shadow-xl backdrop-blur transition-all duration-500 sm:block',
                    youLit ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
                )}
            >
                {step === 3
                    ? "Done: launch post, 200 scored leads, outreach ready and the checkout bug is fixed."
                    : "Launch v2 next week and fill the pipeline."}
            </div>
        </div>
    )
}

export default OrbitHub
